import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useSubscription } from '@apollo/client';
import {
  GET_CALL_SESSION,
  ON_CALL_STATE_UPDATE,
  REQUEST_HUMAN_TRANSFER,
  HANGUP_CALL,
} from '../../graphql/operations';
import TranscriptStream from './TranscriptStream';
import useTranscriptStream from './useTranscriptStream';
import './ActiveCallDrawer.css';

function maskPhone(value) {
  if (!value) return '';
  const digits = String(value).replace(/[^0-9+]/g, '');
  if (digits.length <= 4) return digits;
  return `${digits.slice(0, 4)}…${digits.slice(-4)}`;
}

// Backend emits authState='locked' (voice-handler.js); 'locked_out' is the
// older name that was never updated here. Both map to the blocked pill so
// either backend revision renders correctly. Same for 'pending' which the
// backend emits while attempts remain — it should read as in-progress, not
// unknown.
function authStateClass(state) {
  switch (state) {
    case 'authenticated': return 'auth-pill auth-pill-ok';
    case 'locked':
    case 'locked_out':
    case 'failed':
      return 'auth-pill auth-pill-blocked';
    case 'authenticating':
    case 'pending':
      return 'auth-pill auth-pill-pending';
    default: return 'auth-pill auth-pill-unknown';
  }
}

function authStateLabel(state) {
  switch (state) {
    case 'authenticated': return 'Authenticated';
    case 'authenticating': return 'In auth';
    case 'pending': return 'Pre-auth';
    case 'failed': return 'Auth failed';
    case 'locked':
    case 'locked_out':
      return 'Locked out';
    case 'unauthenticated': return 'Pre-auth';
    default: return state || 'Unknown';
  }
}

function elapsedSince(iso) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '—';
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function ActiveCallDrawer({ callId, onCleared }) {
  const [transferReason, setTransferReason] = useState('');
  const [confirmAction, setConfirmAction] = useState(null); // 'transfer' | 'hangup' | null
  const [actionError, setActionError] = useState(null);

  // PR #93 review round 11 Medium 2: ON_CALL_STATE_UPDATE only fires for
  // AppSync mutations. The outbound flow's Twilio status callbacks land on
  // the HTTP /voice/twilio/status route and update the row directly via
  // DynamoDB — no AppSync mutation, no subscription fanout. Without a
  // poll, the drawer's snapshot goes stale between the initial GET and
  // whatever subscription event (if any) eventually fires. Poll every 3s
  // while the call is non-terminal and stop polling once `endedAt` lands.
  // useLiveCalls drives the list; the drawer drives its own detail.
  const { data: snapshotData, loading: snapshotLoading, error: snapshotError, refetch: refetchSession, startPolling, stopPolling } = useQuery(
    GET_CALL_SESSION,
    {
      variables: { callId },
      skip: !callId,
      fetchPolicy: 'cache-and-network',
    }
  );

  const { data: liveData } = useSubscription(ON_CALL_STATE_UPDATE, {
    variables: { callId },
    skip: !callId,
  });

  const session = liveData?.onCallStateUpdate || snapshotData?.getCallSession || null;

  // Start/stop polling based on whether the selected call is still live.
  // `endedAt` is the canonical live-vs-ended signal (round 11 Low 3 made
  // sure both outbound_completed and outbound_failed set it).
  useEffect(() => {
    if (!callId) return undefined;
    if (session?.endedAt) {
      stopPolling();
      return undefined;
    }
    startPolling(3000);
    return () => stopPolling();
  }, [callId, session?.endedAt, startPolling, stopPolling]);

  const { segments, loading: transcriptLoading, error: transcriptError } = useTranscriptStream(callId);

  const [requestTransferMutation, { loading: transferring }] = useMutation(REQUEST_HUMAN_TRANSFER);
  const [hangupMutation, { loading: hangingUp }] = useMutation(HANGUP_CALL);

  // Reset action state when call selection changes.
  useEffect(() => {
    setTransferReason('');
    setConfirmAction(null);
    setActionError(null);
  }, [callId]);

  const ended = useMemo(() => Boolean(session?.endedAt), [session]);
  const startedAt = session?.createdAt;

  // Tick the duration display every second while the call is live.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (ended || !startedAt) return undefined;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [ended, startedAt]);

  if (!callId) {
    return (
      <div className="call-drawer call-drawer-empty">
        <div className="call-drawer-empty-icon" aria-hidden="true">
          <svg viewBox="0 0 32 32" width="44" height="44" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 16h4l3 8 6-16 3 8h4" />
          </svg>
        </div>
        <h3>Select a call</h3>
        <p>Pick a row from the list to see the live transcript, auth state, and operator controls.</p>
      </div>
    );
  }

  if (snapshotError && !session) {
    return (
      <div className="call-drawer call-drawer-error" role="alert">
        <h3>Couldn't load this call</h3>
        <p>{String(snapshotError.message || snapshotError)}</p>
        <button type="button" onClick={() => refetchSession()}>Retry</button>
      </div>
    );
  }

  if (snapshotLoading && !session) {
    return (
      <div className="call-drawer call-drawer-loading" aria-busy="true">
        <p>Loading call…</p>
      </div>
    );
  }

  async function runTransfer() {
    setActionError(null);
    try {
      await requestTransferMutation({
        variables: { callId, reason: transferReason || 'operator_initiated' },
      });
      setConfirmAction(null);
      setTransferReason('');
      refetchSession();
    } catch (err) {
      setActionError(err?.message || 'Transfer failed');
    }
  }

  async function runHangup() {
    setActionError(null);
    try {
      await hangupMutation({ variables: { callId } });
      setConfirmAction(null);
      refetchSession();
      if (onCleared) onCleared(callId);
    } catch (err) {
      setActionError(err?.message || 'Hang up failed');
    }
  }

  return (
    <div className="call-drawer">
      <header className="call-drawer-header">
        <div className="call-drawer-header-row">
          <h3 className="call-drawer-title">{maskPhone(session?.fromNumber) || callId}</h3>
          <span
            className={authStateClass(session?.authState)}
            aria-label={`Auth state: ${authStateLabel(session?.authState)}`}
          >
            {authStateLabel(session?.authState)}
          </span>
        </div>
        <div className="call-drawer-meta">
          <span>callId <code>{callId}</code></span>
          <span>·</span>
          {!ended && (
            <span className="call-drawer-live-indicator" aria-label="Call is live">
              <span className="call-drawer-live-dot" aria-hidden="true" />
              LIVE
            </span>
          )}
          <span>{ended ? 'Ended' : elapsedSince(startedAt)}</span>
          {session?.transferState && (
            <>
              <span>·</span>
              <span>Transfer: {session.transferState}</span>
            </>
          )}
        </div>
      </header>

      <TranscriptStream
        callId={callId}
        segments={segments}
        loading={transcriptLoading}
        error={transcriptError}
        callEnded={ended}
      />

      <footer className="call-drawer-actions">
        {actionError && (
          <div className="call-drawer-action-error" role="alert">{actionError}</div>
        )}
        <div className="call-drawer-action-row">
          <input
            type="text"
            className="call-drawer-transfer-reason"
            placeholder="Transfer reason (optional)"
            value={transferReason}
            onChange={(event) => setTransferReason(event.target.value)}
            disabled={ended || transferring}
            aria-label="Transfer reason"
          />
          {confirmAction === 'transfer' ? (
            <>
              <button
                type="button"
                className="call-drawer-confirm"
                onClick={runTransfer}
                disabled={transferring}
              >
                {transferring ? 'Transferring…' : 'Confirm transfer'}
              </button>
              <button
                type="button"
                className="call-drawer-cancel"
                onClick={() => setConfirmAction(null)}
                disabled={transferring}
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              className="call-drawer-transfer"
              onClick={() => setConfirmAction('transfer')}
              disabled={ended}
            >
              Transfer to support
            </button>
          )}
          {confirmAction === 'hangup' ? (
            <>
              <button
                type="button"
                className="call-drawer-confirm-danger"
                onClick={runHangup}
                disabled={hangingUp}
              >
                {hangingUp ? 'Ending…' : 'Confirm hang up'}
              </button>
              <button
                type="button"
                className="call-drawer-cancel"
                onClick={() => setConfirmAction(null)}
                disabled={hangingUp}
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              className="call-drawer-hangup"
              onClick={() => setConfirmAction('hangup')}
              disabled={ended}
            >
              Hang up
            </button>
          )}
        </div>
      </footer>
    </div>
  );
}
